import { Grid, styled, Typography,  Box } from "@mui/material";
import _, { isEmpty } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import { Utills } from "../../../../shared/app-configs/utilit";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import CustomTable from "../../../../shared/components/table/custom-table";
import { useFormActions } from "../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../shared/hooks/useApi";
import useRecordsStore, {
  Pagination,
} from "../../../../shared/hooks/useRecordStore";
import { extractTEIdFromEmail } from "../../../../shared/utils/user-utils";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { getColumnsBySource } from "../../../transaction-certificates/tc-dashboard/cbLandingPageTable.config";
import {
  buyerSellerDetailsService,
  mlbuyerSellerDetailsService,
} from "../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";

export const PageWrapper = styled("div")({
  padding: "0 25px 0px 25px",
  display: "grid",
  overflowY: "auto",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
});

const MaterialLedgerDashboards = () => {
  const formRef = useRef<any>(null);
  const skipPaginationFetchRef = useRef(false);
  const navigate = useNavigate();
  const { resetAllDatas } = useFormActions("");
  const [productCategory, setProductCategory] = useState<any[]>([]);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const { setDelayReason, setTCIssueDate } = useTransactionCertificateStore();

  const { setNewTCRequested } = useTransactionCertificateStore();

  const { executeGet } = useApi();

  const { cbLicensingCode, getUserInfo } = useTransactionCertificateStore();
  const [singleValue, setSingleValue] = React.useState<string | number>("");
  const { materialLedgerData, fetchRecords, selectedSource, isLoading } =
    useRecordsStore();
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const { TE_ID, RM_CODE } = useParams();
  const [rowCount, setRowCount] = useState<number>(0);
  const resetPaginationState = () => {
    const nextPagination = {
      pageIndex: 0,
      pageSize: pagination.pageSize,
    };

    if (
      pagination.pageIndex !== nextPagination.pageIndex ||
      pagination.pageSize !== nextPagination.pageSize
    ) {
      skipPaginationFetchRef.current = true;
      setPagination(nextPagination);
    }

    return nextPagination;
  };

  const filteredData = formRef?.current?.values?.coTeID
    ? materialLedgerData
    : [];
  const columns = useMemo(
    () => getColumnsBySource("materialLedger"),
    [selectedSource]
  );
  const [countries, setCountries] = useState([]);

  const [isResetEnabled, setResetEnable] = useState(false);
  const [visible, setVisible] = useState({
    coName: true,
    coTeId: true,
    coNameLabel: true,
    coTeIdLabel: true,
  });
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<
    any[]
  >([]);
  const [filteredRMCodes, setFilteredRMCodes] = useState<any[]>([]);
  const [disable, setDisable] = useState({
    coName: false,
    coTeId: false,
    rmCode: true,
    // productCategory: true,
    // productDetail: true,
  });
  // State to track if user is a CO (Certified Organization)
  const [isCOUser, setIsCOUser] = useState(false);
  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [pcPdWarningShow, setPcPdWarningShow] = useState<boolean>(false);
  const fetchRawMaterialOptions = async () => {
    try {
      const result: any = await dtrackItService.getRawMaterialCodes();
      const options = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setRawMaterials(options);
    } catch (err) {
      console.error("Error fetching raw material codes", err);
    }
  };
  useEffect(() => {
    if (isEmpty(rawMaterials)) {
      fetchRawMaterialOptions();
    }
  }, []);

  // Separate useEffect to handle RM_CODE pre-selection when rawMaterials are loaded
  useEffect(() => {
    if (RM_CODE && rawMaterials.length > 0 && formRef.current) {
      const currentRmCode = formRef.current.values?.rmCode;
      // Only set if not already set to avoid overriding user selections
      if (!currentRmCode) {
        formRef.current.setFieldValue("rmCode", RM_CODE);
      }
    }
  }, [rawMaterials, RM_CODE]);
  useEffect(() => {
    setPcPdWarningShow(
      RM_CODE !== null &&
        RM_CODE !== undefined &&
        (_.isEmpty(formRef?.current?.values?.productCategory) ||
          _.isEmpty(formRef?.current?.values?.productDetail))
    );
  }, [
    formRef?.current?.values?.productCategory,
    formRef?.current?.values?.productDetail,
    RM_CODE,
  ]);
  useEffect(() => {
    if (isCOUser) {
      setFilteredRMCodes(rawMaterials);
      setDisable((prev) => ({
        ...prev,
        coTeId: true,
        rmCode: false,
        productCategory: false,
        productDetail: false,
      }));
    } else if (TE_ID && !isCOUser) {
      setFilteredRMCodes(rawMaterials);
    }
  }, [isCOUser, rawMaterials]);

  // CO User Detection and Auto-fill Logic
  useEffect(() => {
    const checkCOUser = async () => {
      const userEmail = cbLicensingCode;
      if (userEmail) {
        const extractedTEId = extractTEIdFromEmail(userEmail);
        if (extractedTEId) {
          setIsCOUser(true);

          // Auto-fill CO fields with extracted TE-ID
          if (formRef.current) {
            formRef.current.setFieldValue("coTeID", extractedTEId);
          } else {
            // Retry after a short delay if form not ready
            setTimeout(() => {
              if (formRef.current) {
                formRef.current.setFieldValue("coTeID", extractedTEId);
              }
            }, 100);
          }

          // Fetch organization details using extracted TE-ID
          try {
            const queryParams: Record<string, any> = {
              query_param: extractedTEId,
              offset: 0,
            };
            const response = await buyerSellerDetailsService.getTEDetails(
              queryParams
            );
            if (response && response.data.length > 0) {
              const details = response.data[0];
              // Populate organization name
              if (formRef.current) {
                formRef.current.setFieldValue("coName", details.account_name);
              }
            }
          } catch (error) {
            console.error("Error fetching CO organization details:", error);
            // Fields remain editable if API fails
          }

          // Set fields as disabled and hidden for CO users
          setVisible((prev) => ({
            ...prev,
            coName: false,
            coTeId: false,
            coNameLabel: false,
            coTeIdLabel: false,
          }));
          setDisable((prev) => ({
            ...prev,
            coName: true,
            coTeId: true,
          }));
        }
      }
    };

    checkCOUser();
  }, [cbLicensingCode]);

  useEffect(() => {
    if (TE_ID && !isCOUser) {
      // Only process URL parameters if user is not a CO
      // 🔹 STEP 1: IMMEDIATELY set TE-ID field (following inventory-updates pattern)
      if (formRef.current) {
        formRef.current.setFieldValue("coTeID", TE_ID);

        // Pre-select RM_CODE if available
        if (RM_CODE && rawMaterials.length > 0) {
          formRef.current.setFieldValue("rmCode", RM_CODE);
        }
      } else {
        // Retry after a short delay if form not ready
        setTimeout(() => {
          if (formRef.current) {
            formRef.current.setFieldValue("coTeID", TE_ID);

            if (RM_CODE && rawMaterials.length > 0) {
              formRef.current.setFieldValue("rmCode", RM_CODE);
            }
          }
        }, 100);
      }

      // 🔹 STEP 2: Fetch organization details (following inventory-updates pattern exactly)
      const fetchDetails = async () => {
        try {
          const response = await fetchTEIdDetails(TE_ID, 0, "query"); // Use correct function with TE_ID
          if (response && response.length > 0) {
            const details = response[0];

            // Populate ALL organization fields like inventory-updates does
            if (formRef.current) {
              formRef.current.setFieldValue("coName", details.account_name);
            }

            setVisible((prev) => ({
              ...prev,
              coName: false,
              coTeId: false, // Show as text field since we have the value
              coTeIdLabel: false,
            }));
            setDisable((prev) => ({
              ...prev,
              coName: true,
              coTeId: true, // Disable since it came from URL
            }));
          } else {
            // No organization details found - still show TE-ID as populated
            setVisible((prev) => ({
              ...prev,
              coTeId: false, // Show as text field since we have the value
              coTeIdLabel: false,
            }));
            setDisable((prev) => ({
              ...prev,
              coTeId: true, // Disable since it came from URL
            }));
          }
        } catch (error) {
          console.error("Error fetching organization details:", error);
          // Still update UI to show TE-ID as populated even if API fails
          setVisible((prev) => ({
            ...prev,
            coTeId: false,
            coTeIdLabel: false,
          }));
          setDisable((prev) => ({
            ...prev,
            coTeId: true,
          }));
        }
      };

      // 🔹 STEP 3: Auto-submit form with TE_ID (regardless of organization details)
      const autoSubmitForm = async () => {
        const nextPagination = resetPaginationState();
        await getCBLandingPageRecords(
          {
            coTeID: TE_ID,
            startDate: null,
            endDate: null,
            productCategory: [],
            productDetail: [],
            rawMaterialCode: RM_CODE,
            rmCode: RM_CODE,
          },
          nextPagination
        );
        setResetEnable(true);
      };

      // Execute steps with small delays to ensure proper timing
      const timer1 = setTimeout(() => {
        fetchDetails(); // Fetch organization details
      }, 100);

      const timer2 = setTimeout(() => {
        autoSubmitForm(); // Auto-submit with TE_ID
      }, 200);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else if (RM_CODE && !TE_ID && rawMaterials.length > 0) {
      // If only RM_CODE is present, pre-select it but don't auto-submit
      if (formRef.current) {
        formRef.current.setFieldValue("rmCode", RM_CODE);
      }
    }
  }, [TE_ID, RM_CODE, cbLicensingCode, rawMaterials, isCOUser]);

  const getProductCategory = async () => {
    try {
      const result: any = await dtrackItService.getProductCategory();

      // Utills.cl(`result ${JSON.stringify(result)}`)

      const prdtCategory = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      Utills.cl(`prdtCategory ${JSON.stringify(prdtCategory)}`);
      setProductCategory(prdtCategory);
    } catch (err) {
      Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };

  const getProductDetails = async () => {
    try {
      const result: any = await dtrackItService.getProductDetailsCode();

      Utills.cl(`result getProductDetails ${JSON.stringify(result)}`);

      const prdtDetails = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      Utills.cl(`prdtDetails ${JSON.stringify(prdtDetails)}`);
      setProductDetails(prdtDetails);
    } catch (err) {
      Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
    }
  };

  // useEffect to refetch data when pagination changes
  useEffect(() => {
    if (skipPaginationFetchRef.current) {
      skipPaginationFetchRef.current = false;
      return;
    }
    if (formRef.current && isResetEnabled) {
      // Only refetch if there's been a search already performed
      const formValues = formRef.current.values;
      if (formValues.coTeID && formValues.rmCode) {
        getCBLandingPageRecords(formValues);
      }
    }
  }, [pagination.pageIndex, pagination.pageSize]);
  const getRawMatStandardCodes = async () => {
    const response = await executeGet(
      TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
    );
    if (response?.data) {
      setCertifiedRawMaterialCodes(response.data);
    }
  };
  useEffect(() => {
    setDelayReason(false);
    setTCIssueDate("");
    resetAllDatas();
    getProductDetails();
    getProductCategory();
    getRawMatStandardCodes();
  }, []);

  // Normalize standards string to a list of comparable codes (handles "(RMS)" and "RMS, RWS" formats)
  const extractApprovedStandards = (rawStandards: any): string[] => {
    if (!rawStandards) return [];
    const standardsString = Array.isArray(rawStandards)
      ? rawStandards.join(",")
      : String(rawStandards);

    const parenthesizedCodes =
      [...standardsString.matchAll(/\(([^)]+)\)/g)].map((match) =>
        match[1].trim().toUpperCase()
      ) || [];

    const fallbackCodes = standardsString
      .split(/[,/]/)
      .map((token) => token.trim())
      .filter((token) => token && !token.includes(" "))
      .filter((token) => /^[A-Za-z0-9-]+$/.test(token))
      .map((token) => token.toUpperCase());

    const combined = parenthesizedCodes.length
      ? [...parenthesizedCodes, ...fallbackCodes]
      : fallbackCodes;

    return [...new Set(combined)];
  };

  const updateFilteredRMCodes = (value: any) => {
    const codes = extractApprovedStandards(value?.standards);
    const approvedSet = new Set(codes);
    const filtered = certifiedRawMaterialCodes.filter((item) =>
      item?.standard
        .split("/")
        .some((part: any) =>
          approvedSet.has(String(part).trim().toUpperCase())
        )
    );
    const validMaterialCodes = filtered.map((item) => item.material_code);
    setFilteredRMCodes(
      rawMaterials.filter((item) => validMaterialCodes.includes(item.value))
    );
  };

  const handleCoNameValueChange = (value: any) => {
    if (value) {
      updateFilteredRMCodes(value);
      formRef.current.setFieldValue("coName", value.account_name);
      formRef.current.setFieldValue("coTeID", value.TE_ID);
      formRef.current.setFieldValue("rmCode", "");
      formRef.current.setFieldValue("productCategory", "");
      formRef.current.setFieldValue("productDetail", "");
      if (!RM_CODE) {
        formRef.current.setFieldValue("rmCode", "");
      }
      setVisible((prev) => ({
        ...prev,
        coTeId: false,
        coNameLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: true,
        rmCode: !value?.TE_ID,
        productCategory: !value?.TE_ID,
        productDetail: !value?.TE_ID,
      }));
    } else {
      setFilteredRMCodes([]);
      formRef.current.setFieldValue("coName", "");
      formRef.current.setFieldValue("coTeID", "");
      formRef.current.setFieldValue("rmCode", "");
      formRef.current.setFieldValue("productCategory", "");
      formRef.current.setFieldValue("productDetail", "");
      setVisible((prev) => ({
        ...prev,
        coTeId: true,
        coNameLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: false,
        rmCode: true,
        productCategory: true,
        productDetail: true,
      }));
    }
  };

  const handleCoTeIdValueChange = (value: any) => {
    if (value) {
      updateFilteredRMCodes(value);
      formRef.current.setFieldValue("coTeID", value.TE_ID);
      formRef.current.setFieldValue("coName", value.account_name);
      formRef.current.setFieldValue("rmCode", "");
      formRef.current.setFieldValue("productCategory", "");
      formRef.current.setFieldValue("productDetail", "");
      setVisible((prev) => ({
        ...prev,
        coName: false,
        coTeIdLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: true,
        rmCode: !value?.TE_ID,
        productCategory: !value?.TE_ID,
        productDetail: !value?.TE_ID,
      }));
    } else {
      setFilteredRMCodes([]);
      formRef.current.setFieldValue("coTeID", "");
      formRef.current.setFieldValue("coName", "");
      formRef.current.setFieldValue("rmCode", "");
      formRef.current.setFieldValue("productCategory", "");
      formRef.current.setFieldValue("productDetail", "");
      setVisible((prev) => ({
        ...prev,
        coName: true,
        coTeIdLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: false,
        rmCode: true,
        productCategory: true,
        productDetail: true,
      }));
    }
  };

  const fetchTENameDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: "",
        offset: offset,
        name: query,
        tcCbLicensingCode: cbLicensingCode, //"CB-CUC"
      };
      const response = await mlbuyerSellerDetailsService.getMLCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by name:", error);
      return [];
    }
  };

  const fetchTEIdDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: query,
        offset: offset,
        name: "",
        tcCbLicensingCode: cbLicensingCode, //"CB-CUC"
      };
      const response = await mlbuyerSellerDetailsService.getMLCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by ID:", error);
      return [];
    }
  };

  const formConfig = {
    pageName: "cb-materialLedger-landing-page",
    initialValues: {
      coName: "",
      coTeID: "",
      rmCode: "",
      productDetail: "",
      startDate: null,
      endDate: null,
      productCategory: "",
      outputProductDetail: [],
      outputProductCategory: [],
    },
    validationSchema: Yup.object().shape({
      coName: Yup.string().nullable(),
      coTeID: Yup.string().required("TE-ID is required"),
      rmCode: Yup.string().required("Raw Material Code is required"),
      startDate: Yup.string().nullable(),
      endDate: Yup.string().nullable(),
      productCategory: Yup.string().required("Product Category is required"),
      productDetail: Yup.string().required("Product Detail is required"),
    }),
    sections: [
      {
        fields: [
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: false,
            visible: () => visible.coName && !isCOUser,
            props: {
              placeholder: "Search Organization Name",
              variant: "outlined",
              fullWidth: true,
              displayKey: "account_name",
              onChange: handleCoNameValueChange,
              onSearch: (query: string, offset: number) => {
                return fetchTENameDetails(query, offset, "name");
              },
            },
          },
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: false,
            visible: () => !visible.coName || isCOUser,
            disabled: disable.coName || isCOUser,
            props: {
              placeholder: "Enter Organization Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organisation TE-ID",
            name: "coTeID",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: () => visible.coTeId && !isCOUser,
            props: {
              placeholder: "Search by TE-ID",
              variant: "outlined",
              fullWidth: true,
              displayKey: "TE_ID",
              restrictInputFormat: visible.coTeIdLabel,
              onChange: handleCoTeIdValueChange,
              onSearch: (query: string, offset: number) => {
                return fetchTEIdDetails(query, offset, "query");
              },
            },
          },
          {
            label: "Certified Organisation TE-ID",
            name: "coTeID",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: () => !visible.coTeId || isCOUser,
            disabled: disable.coTeId || isCOUser,
            props: {
              placeholder: "Enter TE-ID",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Raw Material Code",
            name: "rmCode",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            disabled: disable?.rmCode,
            required: true,
            props: {
              enableSearch: true,
              placeholder: "Select Raw Material Code",
              variant: "outlined",
              options: filteredRMCodes,
            },
          },
          {
            label: "Input Product Category",
            name: "productCategory",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 3 },
            disabled: false,
            required: true,
            props: {
              options: productCategory,
              placeholder: "Select Product Category",
            },
          },
          {
            label: "Input Product Detail",
            name: "productDetail",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 3 },
            disabled: false,
            required: true,
            props: {
              options: productDetails,
              placeholder: "Select Product Detail",
            },
          },
        ],
      },
      {
        title: "Filter By",
        collapsible: true,
        fields: [
          {
            label: "Output Product Category",
            name: "outputProductCategory",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: {
              multiple: true,
              options: productCategory,
              placeholder: "Select Output Product Category",
            },
          },
          {
            label: "Output Product Detail Description",
            name: "outputProductDetail",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: {
              multiple: true,
              options: productDetails,
              placeholder: "Select Output Product Detail Description",
            },
          },
          {
            label: "Transaction Start Date",
            name: "startDate",
            type: "date",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: "Select Start Date",
            },
          },
          {
            label: "Transaction End Date",
            name: "endDate",
            type: "date",
            gridSize: { xs: 12, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: "Select End Date",
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      const nextPagination = resetPaginationState();
      await getCBLandingPageRecords(values, nextPagination);
    },
  };

  const getCBLandingPageRecords = async (
    values: any,
    paginationOverride?: Pagination
  ) => {
    const activePagination = paginationOverride ?? pagination;
    try {
      if (values.startDate != null || values.endDate != null) {
        setResetEnable(true);
      }
      await fetchRecords({
        source: "materialLedger",
        teId: values.coTeID,
        rawMaterialCode: values.rmCode?.length > 0 ? values.rmCode : RM_CODE,
        startDate: values.startDate,
        endDate: values.endDate,
        productCategory: values.productCategory ? [values.productCategory] : [],
        productDetail: values.productDetail ? [values.productDetail] : [],
        outputProductCategory: values.outputProductCategory,
        outputProductDetail: values.outputProductDetail,
        tcCbLicensingCode: cbLicensingCode,
        pagination: activePagination,
        rowCount: rowCount,
        setRowCount: setRowCount,
      });
    } catch (error) {
      Utills.cl("Error fetching data: ", error);
    }
  };

  const handleRowClick = () => {
    setNewTCRequested(false);
  };

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
      setResetEnable(true);
    }
  };

  const handleReset = async () => {
    // Reset form
    formRef.current.resetForm();

    // Clear URL parameters by navigating to base URL
    navigate("/ml/material-ledger");

    // Reset visibility and disable states only for non-CO users
    if (!isCOUser) {
      setVisible({
        coName: true,
        coTeId: true,
        coNameLabel: true,
        coTeIdLabel: true,
      });

      setDisable({
        coName: false,
        coTeId: false,
        rmCode: true,
        // productCategory: true,
        // productDetail: true,
      });
    } else {
      // For CO users, re-populate their organization details
      const userEmail = cbLicensingCode;
      if (userEmail) {
        const extractedTEId = extractTEIdFromEmail(userEmail);
        if (extractedTEId) {
          setTimeout(async () => {
            if (formRef.current) {
              formRef.current.setFieldValue("coTeID", extractedTEId);

              // Refetch organization details
              try {
                const queryParams: Record<string, any> = {
                  query_param: extractedTEId,
                  offset: 0,
                };
                const response = await buyerSellerDetailsService.getTEDetails(
                  queryParams
                );
                if (response && response.data.length > 0) {
                  const details = response.data[0];
                  setFilteredRMCodes(rawMaterials);
                  setDisable((prev) => ({
                    ...prev,
                    coTeId: true,
                    rmCode: false,
                    productCategory: false,
                    productDetail: false,
                  }));
                  formRef.current.setFieldValue("coName", details.account_name);
                }
              } catch (error) {
                console.error(
                  "Error refetching CO organization details:",
                  error
                );
              }
            }
          }, 100);
        }
      }
    }
    resetPaginationState();
    setRowCount(0);
    setResetEnable(false);

    // Clear the table data by not calling getCBLandingPageRecords
    // The user will need to perform a new search
    useRecordsStore.setState((state: any) => ({
      ...state,
      materialLedgerData: [],
      rowCount: 0,
    }));
  };

  return (
    <>
      <PageWrapper>
        <Typography variant="h6" gutterBottom align="left" fontWeight={"600"}>
          Material Ledger Summary
        </Typography>
        <FormBuilder
          formConfig={formConfig}
          onSubmit={formConfig.handleSubmit}
          formRef={formRef}
        />
        {/* </Grid> */}

        <Grid
          item
          md={2}
          lg={2}
          display="flex"
          justifyContent={"flex-end"}
          alignItems="center"
        >
          <StyledButton onClick={handleSubmit}>Search</StyledButton>
          {isResetEnabled ? (
            <StyledButton onClick={handleReset}>Reset</StyledButton>
          ) : null}
        </Grid>

        {/* </Grid> */}
        {pcPdWarningShow ? (
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "#FFF3E0",
              borderRadius: 1,
              border: "1px solid #FFB74D",
            }}
          >
            <Typography color="warning.dark">
              Please select the required product category or product details from the filters to search for appropriate ledger
            </Typography>
          </Box>
        ) : (
          <CustomTable
            key={selectedSource}
            columns={columns}
            data={filteredData?.map((record) => {
              return {
                ...record,
                productCategoryDesc: productCategory?.find(
                  (pc: any) => pc?.value === record?.productCategory
                )?.label,
                productDetailDesc: productDetails?.find(
                  (pd: any) => pd?.value === record?.productDetail
                )?.label,
                certifiedRmCodeDesc: rawMaterials?.find(
                  (rm: any) => rm?.value === record?.certifiedRmCode
                )?.label,
                outputProductCategoryDesc: productCategory?.find(
                  (pc: any) => pc?.value === record?.outputProductCategory
                )?.label,
                outputProductDetailDesc: productDetails?.find(
                  (pd: any) => pd?.value === record?.outputProductDetail
                )?.label,
                balance:Number(record?.balance).toFixed(2),
                certifiedWeightBought:Number(record?.certifiedWeightBought).toFixed(2),
                certifiedWeightSold: Number(record?.certifiedWeightSold).toFixed(2),
                outputCertifiedWeightSold:Number(record?.outputCertifiedWeightSold).toFixed(2),
              };
            })}
            title="TransactionData"
            enableTopToolbar={true}
            enableBottomToolbar={true}
            // enableColumnOrdering={false}
            paginations={pagination} // Pass current pagination state
            setPaginations={setPagination}
            rowCount={rowCount}
            isLoadingTodos={isLoading}
            onClickRow={handleRowClick}
            enableRowActions={false}
          />
        )}
      </PageWrapper>
    </>
  );
};

export default MaterialLedgerDashboards;
